import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { StarRating, PartnerButtons, RecipeCard, ComplianceNote, Badge } from "@/components/kit";
import { Clock, GlassWater, Sprout, Wrench, ShoppingBag, Gift } from "lucide-react";
import { toast } from "sonner";

export default function RecipeDetail() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  useEffect(() => { window.scrollTo(0, 0); api.get(`/recipes/${id}`).then((x) => setR(x.data)); }, [id]);
  if (!r) return <div className="p-12 text-center text-muted-foreground">Loading recipe…</div>;

  return (
    <div>
      <div className="relative h-72 overflow-hidden lg:h-96">
        <img src={r.image} alt={r.name} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
        <div className="absolute bottom-0 mx-auto w-full max-w-5xl px-6 pb-6">
          <span className="overline text-primary">{r.spirit} · {r.category}</span>
          <h1 className="mt-2 font-display text-4xl lg:text-6xl">{r.name}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
            <StarRating value={r.rating} /><span className="flex items-center gap-1 text-muted-foreground"><Clock size={14} /> {r.prep}</span>
            <Badge>{r.difficulty}</Badge><span className="text-muted-foreground">{r.profile}</span>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-5xl gap-8 px-6 py-8 lg:grid-cols-[1fr_320px]">
        <div className="space-y-8">
          <section>
            <h2 className="mb-3 font-display text-2xl">Ingredients</h2>
            <ul className="divide-y divide-border rounded-xl border border-border">
              {r.ingredients.map((i, k) => <li key={k} className="flex justify-between px-4 py-3 text-sm"><span>{i.item}</span><span className="font-semibold text-primary">{i.amt}</span></li>)}
            </ul>
          </section>
          <section>
            <h2 className="mb-3 font-display text-2xl">Instructions</h2>
            <ol className="space-y-3">
              {r.steps.map((s, k) => <li key={k} className="flex gap-3 text-sm"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">{k + 1}</span><span className="pt-0.5">{s}</span></li>)}
            </ol>
          </section>
          <div className="grid grid-cols-2 gap-4">
            <InfoBox icon={GlassWater} title="Glassware" text={r.glass} />
            <InfoBox icon={Sprout} title="Garnish" text={r.garnish} />
            <InfoBox icon={Wrench} title="Tools needed" text={r.tools.join(", ")} />
            <InfoBox icon={Gift} title="Substitutions" text="Swap in your favorite premium spirit — our concierge adjusts the balance." />
          </div>
          {r.related?.length > 0 && (
            <section>
              <h2 className="mb-3 font-display text-2xl">Related recipes</h2>
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">{r.related.map((x) => <RecipeCard key={x.id} r={x} />)}</div>
            </section>
          )}
        </div>

        {/* sticky shopping panel */}
        <aside className="space-y-4 lg:sticky lg:top-20 lg:self-start">
          <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
            <p className="font-display text-xl">Pick your drinks, we'll provide the ingredients.</p>
            <p className="mt-1 text-sm text-muted-foreground">Shop the full recipe in one tap.</p>
            <button data-testid="buy-ingredients-btn" onClick={() => toast.success("Ingredients added", { description: "Routed to partners for delivery or pickup." })}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground"><ShoppingBag size={16} /> Buy Ingredients</button>
          </div>
          {r.products?.map((p) => (
            <div key={p.id} className="rounded-xl border border-border bg-card p-4">
              <div className="flex gap-3"><img src={p.image} alt="" className="h-16 w-16 rounded-lg object-cover" /><div><Link to={`/ratings/${p.id}`} className="text-sm font-semibold hover-underline">{p.name}</Link><StarRating value={p.rating} size={12} /></div></div>
              <div className="mt-3"><PartnerButtons partners={p.partners} compact /></div>
            </div>
          ))}
          <ComplianceNote />
        </aside>
      </div>
    </div>
  );
}

function InfoBox({ icon: Icon, title, text }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-sm font-semibold"><Icon size={15} className="text-primary" /> {title}</div>
      <p className="mt-1 text-sm text-muted-foreground">{text}</p>
    </div>
  );
}
